import React, { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import Modal from './Modal';

const COLUMNS = [
  { id: 'name', label: 'Player', title: 'Player name', type: 'string', sticky: true, tooltipAlign: 'left' },
  { id: 'pickAccuracy', label: 'Acc', title: 'Overall pick accuracy', type: 'number', accuracy: true, percent: true },
  { id: 'triplePlayWinsPercentage', label: 'TP', title: 'Triple play win rate', type: 'number', accuracy: true, percent: true },
  { id: 'homePickPercentage', label: 'Home', title: 'Share of picks on the home team', type: 'number', percent: true },
  { id: 'cowboysPickRate', label: 'DAL', title: 'How often you picked the Cowboys when they were playing', type: 'number', percent: true },
  { id: 'cowboysPickPercentage', label: 'DAL Acc', title: 'How often you picked correctly when the Cowboys were playing', type: 'number', accuracy: true, percent: true },
  { id: 'lonerPicks', label: 'Loners', title: 'Picks no one else made', type: 'number' },
];

const getSortValue = (player, columnId) => {
  if (columnId === 'name') return player.name || '';
  return player[columnId] || 0;
};

const accuracyClass = (value) => (value < 50 ? 'text-red-500' : 'text-green-500');

const SortableHeader = ({ column, sort, onSort }) => {
  const active = sort.id === column.id;
  const SortIcon = active ? (sort.dir === 'asc' ? FaSortUp : FaSortDown) : null;
  const tooltipPosition = column.tooltipAlign === 'left'
    ? 'left-0'
    : 'left-1/2 -translate-x-1/2';
  const [hideTooltip, setHideTooltip] = useState(false);

  return (
    <th
      className={`relative group py-2 px-1.5 text-xs font-semibold whitespace-nowrap ${
        column.sticky ? 'sticky left-0 z-20 bg-gray-800' : ''
      }`}
      onMouseLeave={() => setHideTooltip(false)}
    >
      <button
        type="button"
        onClick={(event) => {
          onSort(column);
          setHideTooltip(true);
          event.currentTarget.blur();
        }}
        className="inline-flex items-center gap-0.5 text-gray-300 hover:text-white"
        aria-label={`Sort by ${column.title}`}
      >
        {column.label}
        {SortIcon ? <SortIcon className="text-[10px] text-accent" /> : null}
      </button>
      <span
        role="tooltip"
        className={`pointer-events-none absolute top-full z-30 mt-1 w-max max-w-[11rem] rounded bg-gray-900 px-2 py-1 text-left text-xs font-normal text-gray-100 outline outline-1 outline-gray-600 ${
          hideTooltip ? 'hidden' : 'hidden group-hover:block'
        } ${tooltipPosition}`}
      >
        {column.title}
      </span>
    </th>
  );
};

const PlayerStats = ({ throughWeek }) => {
  const [players, setPlayers] = useState([]);
  const [highestWeek, setHighestWeek] = useState(throughWeek || 0);
  const [modalVisible, setModalVisible] = useState(false);
  const [lonerPickDetails, setLonerPickDetails] = useState([]);
  const [sort, setSort] = useState({ id: 'pickAccuracy', dir: 'desc' });

  useEffect(() => {
    setHighestWeek(throughWeek);
  }, [throughWeek]);

  useEffect(() => {
    const fetchPlayers = () => {
      const usersRef = ref(database, 'users');
      const picksRef = ref(database, 'picks');
      const gamesRef = ref(database, 'games');

      onValue(ref(database, 'games'), (snapshot) => {
        const gamesData = snapshot.val();
        if (!gamesData) {
          setHighestWeek(0);
          return;
        }
        const completedWeeks = Object.values(gamesData)
          .filter(game => game.winner && !game.active)
          .map(game => game.weekNumber);

        const weekCounts = completedWeeks.reduce((acc, week) => {
          acc[week] = (acc[week] || 0) + 1;
          return acc;
        }, {});

        const validWeeks = Object.keys(weekCounts).filter(week => {
          return weekCounts[week] === Object.values(gamesData).filter(game => game.weekNumber === parseInt(week)).length;
        });

        setHighestWeek(validWeeks.length ? Math.max(...validWeeks.map(Number)) : 0);
      });

      onValue(usersRef, (snapshot) => {
        const usersData = snapshot.val();
        const playersList = Object.keys(usersData).map((id) => ({
          id,
          name: usersData[id].name,
          cowboysPicks: 0,
          cowboysPickRate: 0,
          correctCowboysPicks: 0,
          triplePlayWins: 0,
          triplePlayWinsPercentage: 0,
          homePicks: 0,
          awayPicks: 0,
          homePickPercentage: 0,
          awayPickPercentage: 0,
          totalPicks: 0,
          correctPicks: 0,
          pickAccuracy: 0,
          lonerPicks: 0,
          lonerPickDetails: [],
        }));

        onValue(picksRef, (snapshot) => {
          const picksData = snapshot.val();

          onValue(gamesRef, (snapshot) => {
            const gamesData = snapshot.val();

            const calculateCowboysPicks = (player, pick, gameDetails) => {
              const homeTeam = gameDetails?.homeTeam;
              const awayTeam = gameDetails?.awayTeam;

              if (homeTeam === 'Cowboys' || awayTeam === 'Cowboys') {
                player.totalCowboysGames = (player.totalCowboysGames || 0) + 1;

                if ((pick === 'home' && homeTeam === 'Cowboys') ||
                    (pick === 'away' && awayTeam === 'Cowboys')) {
                  player.cowboysPicks += 1;
                }

                if (gameDetails.winner === pick) {
                  player.correctCowboysPicks = (player.correctCowboysPicks || 0) + 1;
                }
              }
              player.cowboysPickPercentage = Math.round(((player.correctCowboysPicks || 0) / (player.totalCowboysGames || 1)) * 100);
              player.cowboysPickRate = Math.round(((player.cowboysPicks || 0) / (player.totalCowboysGames || 1)) * 100);
            };

            playersList.forEach((player) => {
              const playerPicks = picksData[player.id];
              if (playerPicks) {
                Object.keys(playerPicks).forEach((gameId) => {
                  const pick = playerPicks[gameId].pick;
                  const gameDetails = gamesData[gameId];

                  if (gameDetails?.weekNumber <= highestWeek) {
                    calculateCowboysPicks(player, pick, gameDetails);

                    if (pick === 'home') {
                      player.homePicks += 1;
                    } else if (pick === 'away') {
                      player.awayPicks += 1;
                    }

                    player.homePickPercentage = Math.round(((player.homePicks || 0) / ((player.homePicks + player.awayPicks) || 1)) * 100);
                    player.awayPickPercentage = Math.round(((player.awayPicks || 0) / ((player.awayPicks + player.homePicks) || 1)) * 100);

                    if (playerPicks[gameId].triplePlay &&
                        (gameDetails.winner === 'push' || (pick === 'home' && gameDetails?.winner === 'home') ||
                          (pick === 'away' && gameDetails?.winner === 'away'))) {
                      player.triplePlayWins += 1;
                      player.triplePlayWinsPercentage = Math.round(((player.triplePlayWins || 0) / (highestWeek || 1)) * 100);
                    }

                    player.totalPicks += 1;
                    if (gameDetails.winner === pick) {
                      player.correctPicks += 1;
                    }
                    player.pickAccuracy = Math.round((player.correctPicks / player.totalPicks) * 100);

                    const otherPicks = Object.values(picksData).filter(p => p[gameId] && p[gameId].pick === pick);
                    if (otherPicks.length === 1) {
                      player.lonerPicks += 1;
                      player.lonerPickDetails.push({
                        team: pick === 'home' ? gameDetails.homeTeam : gameDetails.awayTeam,
                        week: gameDetails.weekNumber,
                        correct: gameDetails.winner === pick,
                      });
                    }
                  }
                });
              }
            });
            setPlayers(playersList);
          });
        });
      });
    };

    fetchPlayers();
  }, [highestWeek]);

  const handleSort = (column) => {
    setSort((prev) => {
      if (prev.id === column.id) {
        return { id: column.id, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { id: column.id, dir: column.type === 'string' ? 'asc' : 'desc' };
    });
  };

  const sortedPlayers = [...players].sort((a, b) => {
    const left = getSortValue(a, sort.id);
    const right = getSortValue(b, sort.id);
    const column = COLUMNS.find((col) => col.id === sort.id);
    const comparison = column?.type === 'string'
      ? String(left).localeCompare(String(right))
      : left - right;
    return sort.dir === 'asc' ? comparison : -comparison;
  });

  const handleLonerPicksClick = (details) => {
    setLonerPickDetails(details);
    setModalVisible(true);
  };

  const renderCell = (player, column, rowClass) => {
    if (column.id === 'name') {
      return (
        <td className={`py-2 px-2 text-left sticky left-0 z-10 ${rowClass}`}>
          {player.name}
        </td>
      );
    }

    if (column.id === 'lonerPicks') {
      return (
        <td className="py-2 px-2 text-center">
          {player.lonerPicks > 0 ? (
            <button
              onClick={() => handleLonerPicksClick(player.lonerPickDetails)}
              className="underline"
            >
              {player.lonerPicks}
            </button>
          ) : (
            player.lonerPicks
          )}
        </td>
      );
    }

    const value = player[column.id] || 0;
    const display = column.percent ? `${value}%` : value;
    const colorClass = column.accuracy ? accuracyClass(value) : '';

    return (
      <td className={`py-2 px-2 text-center tabular-nums ${colorClass}`}>
        {display}
      </td>
    );
  };

  return (
    <div className="max-w-4xl mx-auto pt-4">
      <span className="text-xl mb-2">Player Stats</span>
      <span className="text-sm mb-2"> (through week {highestWeek})</span>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-800">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <SortableHeader
                  key={column.id}
                  column={column}
                  sort={sort}
                  onSort={handleSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => {
              const rowClass = index % 2 === 0 ? 'bg-gray-700' : 'bg-gray-800';
              return (
                <tr key={player.id} className={rowClass}>
                  {COLUMNS.map((column) => (
                    <React.Fragment key={column.id}>
                      {renderCell(player, column, rowClass)}
                    </React.Fragment>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {modalVisible && (
        <Modal onClose={() => setModalVisible(false)}>
          <h2 className="text-xl mb-4">Loner Pick Details</h2>
          <ul>
            {lonerPickDetails.map((detail, index) => (
              <li key={index} className="mb-2">
                <div>Team: {detail.team}</div>
                <div>Week: {detail.week}</div>
                <div>Correct: {detail.correct ? 'Yes' : 'No'}</div>
              </li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
};

export default PlayerStats;
